import { Injectable, Signal, signal } from '@angular/core';
import { LoggingService } from './logging.service';
import {
  parseVersionFromSwMessage,
  UpdateInstallState,
  VersionCheckBackend,
  VersionCheckResult,
  webBackendDisabled,
} from './version-check.dispatch';

// How long to wait for the waiting service worker to reply to a
// `getVersion` message before falling back to the literal 'pending'.
// Deliberately generous: this round-trip only labels the (non-blocking)
// update-available card with a version string, so it is not time-critical.
// A short 500 ms budget reported a "failure" (Sentry
// VersionCheckPwaGetVersionFailed / issue 452) whenever the waiting SW was
// briefly busy precaching the new bundle, even though it would have answered
// moments later. Giving it 30 s means the card shows the real version far
// more often; if the SW still cannot answer within 30 s, that is a genuine
// protocol stall worth reporting, not noise. The fallback path still fires
// Sentry telemetry so chronic SW breakage surfaces (per CLAUDE.md's
// "fallback-to-alternative needs telemetry" rule).
const GET_VERSION_TIMEOUT_MS = 30 * 1000;

// PWA backend for VersionCheckService. Drives the in-app update
// prompt on web/PWA by watching the existing Workbox service worker
// for a `waiting` state — that's how Workbox signals "a new bundle
// has been precached and is ready to take over once every client
// closes". We surface that as updateAvailable: true and post a
// `skipWaiting` message on the user's Update tap; the existing
// `controllerchange` listener in `client/src/index.html` handles the
// reload.
//
// See design-docs/in-app-update-prompt.md §Per-Platform Backends →
// PWA for the architecture and the boot-time-race rationale.
@Injectable({ providedIn: 'root' })
export class VersionCheckPwaBackend implements VersionCheckBackend {

  // Constant Idle: the PWA update path is a skipWaiting + immediate
  // reload, so there is no observable background-download state for the
  // card to show. Present only to satisfy the cross-platform backend
  // contract. See design-docs/in-app-update-prompt.md §installState.
  readonly installState: Signal<UpdateInstallState> = signal(UpdateInstallState.Idle).asReadonly();

  // The waiting SW, set on init (if one was already in `registration.waiting`
  // when the page loaded) and updated by the `updatefound` →
  // `statechange === 'installed'` listener so the flag tracks the
  // latest waiting worker through later background-precaches.
  private waitingWorker: ServiceWorker | null = null;

  // Resolves once the constructor's async probe finishes (or
  // immediately when the backend is self-disabled). getStatusAsync
  // and startUpdateAsync both await this so any caller that reaches
  // the backend before the SW registration has resolved gets a
  // correct answer rather than the constructor-time waiting flag of
  // null. Closes the boot-time race described in the design doc.
  private readonly isReady: Promise<void>;

  constructor(private logging: LoggingService) {
    // Kick off init in the constructor but DO NOT block — Angular
    // service constructors must return synchronously.
    this.isReady = this.initializeAsync().catch(err => {
      // Surfacing rule: SW init failure means no update detection
      // (degraded UX, not a crash), so log + continue. The failure
      // is logged once at init time; subsequent getStatusAsync calls
      // see waitingWorker === null and return updateAvailable: false.
      this.logging.trackSilentProblem('VersionCheckPwaInitFailed', {
        message: (err as Error)?.message,
        name: (err as Error)?.name,
      });
    });
  }

  async getStatusAsync(): Promise<VersionCheckResult> {
    await this.isReady;
    if (!this.waitingWorker) return { updateAvailable: false };
    const availableVersion = await this.requestVersionFromWaitingAsync(this.waitingWorker);
    return { updateAvailable: true, availableVersion };
  }

  async startUpdateAsync(_belowMinimum: boolean): Promise<void> {
    await this.isReady;
    // No waiting SW = nothing to skip — silent no-op, callers already
    // gated on getStatusAsync's updateAvailable.
    const worker = this.waitingWorker;
    if (!worker) return;
    // The existing string-data handler in `client/templates/sw.js`
    // listens for `'skipWaiting'`; on receipt the new SW takes
    // control and fires `controllerchange` on the page, which the
    // existing listener in `client/src/index.html:228-235` turns
    // into a `window.location.reload()`.
    worker.postMessage('skipWaiting');
    // Defensively clear the flag now that we've handed off to the
    // browser. In the normal flow the page reloads almost immediately
    // and this clear is irrelevant. But any caller that bypasses the
    // implicit reload (debug-console invocation, a future code path
    // that swallows controllerchange) would otherwise see a stale
    // `waitingWorker` pointer to a now-activated SW on the next
    // getStatusAsync — and report `updateAvailable: true` for the
    // already-active version. The next genuine `updatefound →
    // installed → waiting` cycle later in the session re-populates
    // the flag if a new SW arrives.
    this.waitingWorker = null;
  }

  // The "Please update" alert's Update button (#6770): skipWaiting + reload already
  // switches at once, so it is the same.
  async startRequiredUpdateAsync(belowMinimum: boolean): Promise<void> {
    await this.startUpdateAsync(belowMinimum);
  }

  // No-op: the PWA flow never reaches the Ready state (skipWaiting
  // reloads the page directly), so there is nothing to "complete".
  // Present only to satisfy the cross-platform backend contract.
  async completeUpdateAsync(_belowMinimum: boolean): Promise<void> {
    // intentionally empty — see installState above
  }

  private async initializeAsync(): Promise<void> {
    // Belt-and-braces self-disable: supportsUpdateChecking() already
    // returns false here (no SW available, or local-dev hostname),
    // so the dispatcher never asks. Guarding here lets direct
    // callers (e.g. debug-console invocations, future test fixtures)
    // get the silent no-op too.
    if (webBackendDisabled(this.platformFlags())) return;

    // navigator.serviceWorker.ready resolves once the page has an
    // active registration. The existing index.html registers
    // `/sw.js` on load — typically registration is already done by
    // the time the dashboard's ngOnInit kicks off, but the await
    // here closes the worker-installed-before-page-loaded race.
    const registration = await navigator.serviceWorker.ready;

    // If the new SW finished installing while the previous page was
    // still controlling, it's already in `registration.waiting` —
    // pick it up so the first getStatusAsync call after page load
    // sees the update.
    if (registration.waiting) {
      this.waitingWorker = registration.waiting;
    }

    // Track LATER waiting workers — Workbox's background precache
    // can install a new SW any time during the session, when the
    // page asks the registration to update or when the browser
    // checks for SW updates on a navigation. Note: this listener
    // only catches updates that fire AFTER `serviceWorker.ready`
    // resolves; on a fresh-first install (no prior controller),
    // `ready` blocks until activation, by which time `updatefound`
    // has already fired and we've missed it. That case still
    // surfaces correct behavior (no waiting worker → updateAvailable
    // false), but the "always tracked" guarantee only holds for the
    // post-init session.
    registration.addEventListener('updatefound', () => {
      const installing = registration.installing;
      if (!installing) return;
      installing.addEventListener('statechange', () => {
        // `installed` is a transient state: per the W3C SW spec a worker
        // only sits in `waiting` if an existing controller is blocking
        // activation. On a fresh first install (no prior controller)
        // the worker passes through `installed → activating → activated`
        // quickly without ever entering `waiting` — so treating every
        // `installed` transition as "now waiting" would mis-report
        // `updateAvailable: true` against an already-active worker.
        // The canonical check is `registration.waiting === installing`.
        if (installing.state === 'installed' && registration.waiting === installing) {
          this.waitingWorker = installing;
        }
      });
    });
  }

  // Round-trips a `getVersion` message to the waiting SW and returns
  // its AppVersion reply. Uses a MessageChannel so the SW's response
  // is routed back over a private port (postMessage on
  // navigator.serviceWorker would broadcast to every controlled
  // client). Falls back to the literal `'pending'` AND fires Sentry
  // telemetry on timeout / malformed reply per CLAUDE.md's
  // "fallback-to-alternative needs telemetry" rule — silent
  // swallowing here would hide chronic SW-protocol breakage that
  // the user never sees but the on-call would want to know about.
  private async requestVersionFromWaitingAsync(worker: ServiceWorker): Promise<string> {
    return new Promise<string>(resolve => {
      const channel = new MessageChannel();
      // shared cleanup so both the timeout and the message paths
      // never leak the port or double-resolve.
      let settled = false;
      const settle = (version: string, reason?: string) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        // closing the port is hygiene; the GC will collect once both
        // ends are unreferenced but explicit close is the contract.
        channel.port1.close();
        if (reason) {
          this.logging.trackSilentProblem('VersionCheckPwaGetVersionFailed', {
            sw: 'waiting',
            reason,
          });
        }
        resolve(version);
      };

      const timer = setTimeout(() => settle('pending', 'timeout'), GET_VERSION_TIMEOUT_MS);

      channel.port1.onmessage = (ev: MessageEvent) => {
        // null = malformed reply (protocol breakage — surface to Sentry);
        // any string = the SW's authoritative answer, even when the
        // string is the literal 'pending' (the SW's own
        // __APP_VERSION__-missing fallback). Splitting null from
        // string here is the load-bearing distinction so telemetry
        // doesn't false-fire on a well-formed 'pending' reply.
        const parsed = parseVersionFromSwMessage(ev.data);
        if (parsed === null) {
          settle('pending', 'malformed-reply');
        } else {
          settle(parsed);
        }
      };

      try {
        worker.postMessage({ type: 'getVersion' }, [channel.port2]);
      } catch (err) {
        // postMessage on a dead worker can throw; treat as a timeout
        // so the caller still gets a useful answer.
        settle('pending', `postMessage threw: ${(err as Error)?.message ?? 'unknown'}`);
      }
    });
  }

  // Reads at call-time so init reflects the live globals. Inside a
  // Capacitor-native build the dispatcher chooses the Android or iOS
  // backend instead, so we never reach this path there — but if a
  // future runtime did inject the PWA backend on a non-browser
  // platform, the disabled check would still no-op cleanly.
  private platformFlags(): { hasServiceWorker: boolean; hostname: string } {
    return {
      hasServiceWorker: typeof navigator !== 'undefined' && !!navigator.serviceWorker,
      hostname: typeof location !== 'undefined' ? location.hostname : '',
    };
  }
}
